import { BookingPosition, PositionContextSummary } from '../types';

/**
 * Aggregiert die Validierungs-Zusammenfassungen aller Positionen einer Buchung
 * und stellt zusammengefasste Meldungen sowie Zähler für Blocker und Warnungen bereit.
 */
export class Validation {
  private blockerCount: number;
  private warningCount: number;
  private readonly summaries: PositionContextSummary[];

  constructor() {
    this.summaries = [];
    this.blockerCount = 0;
    this.warningCount = 0;
  }

  /**
   * Baut eine über alle Positionen zusammengefasste Meldung der blockierenden Regeln,
   * pro Position mit Artikelname und bei mehreren Blockern nummeriert.
   * @returns Zusammengefasste Blocker-Meldung als String.
   */
  public getBlockerMessage(): string {
    return this.summaries
      .map(summary => {
        const bMsg = summary.blocking
          .map((blocker, index) => {
            // Nur nummerieren, wenn mehrere Blocker
            return `${summary.blocking.length > 1 ? index + 1 + '. ' : ''}${
              blocker.message
            }`;
          })
          .join(', ');
        return `${summary.position.article_name}: ${bMsg}\n`;
      })
      .join(', ');
  }

  /**
   * Baut eine über alle Positionen zusammengefasste Meldung der warnenden Regeln,
   * pro Position mit Artikelname und bei mehreren Warnungen nummeriert.
   * @returns Zusammengefasste Warnungs-Meldung als String.
   */
  public getWarningMessage(): string {
    return this.summaries
      .map(summary => {
        const wMsg = summary.warnings
          .map((warning, index) => {
            // Nur nummerieren, wenn mehrere Warnings
            return `${summary.warnings.length > 1 ? index + 1 + '. ' : ''}${
              warning.message
            }`;
          })
          .join(', ');
        return `${summary.position.article_name}: ${wMsg}\n`;
      })
      .join(', ');
  }

  /**
   * Sucht die Zusammenfassung zu einer Position über Referenzgleichheit.
   * @param position Position, deren Zusammenfassung gesucht wird.
   * @returns Passende Zusammenfassung oder null, wenn keine vorhanden ist.
   */
  public getSummary(position: BookingPosition): PositionContextSummary | null {
    // Wenn das auf Dauer zu langsam wird durch Record ersetzen
    // Aktuell nicht, da position.id nicht required ist und ein extra key
    // konstruiert werden müsste, was nicht besonders effizient ist
    return this.summaries.find(s => s.position === position) ?? null;
  }

  /**
   * @returns Gesamtzahl der blockierenden Verletzungen über alle Positionen.
   */
  public getBlockerCount(): number {
    return this.blockerCount;
  }

  /**
   * @returns Gesamtzahl der Warnungen über alle Positionen.
   */
  public getWarningCount(): number {
    return this.warningCount;
  }

  /**
   * @returns True, wenn mindestens eine blockierende Verletzung vorliegt.
   */
  public hasBlockers(): boolean {
    return this.blockerCount > 0;
  }

  /**
   * @returns True, wenn mindestens eine Warnung vorliegt.
   */
  public hasWarnings(): boolean {
    return this.warningCount > 0;
  }

  /**
   * Fügt die Zusammenfassung einer Position hinzu und erhöht die Blocker- und Warnungszähler.
   * @param summary Zusammenfassung einer Position mit ihren blockierenden und warnenden Regeln.
   */
  public addSummary(summary: PositionContextSummary): void {
    this.summaries.push(summary);
    this.blockerCount += summary.blocking.length;
    this.warningCount += summary.warnings.length;
  }
}
